import * as React from 'react'
import {
  IonRouterOutlet,
  IonContent,
  IonIcon,
  IonItem,
  IonList,
  IonMenu,
  IonMenuToggle,
  useIonAlert,
} from '@ionic/react'
import { useHistory, Route, Redirect } from 'react-router-dom'
import { logOut, person, home, desktop } from 'ionicons/icons'

import { AuthContext } from './contexts/authContext'
import { PrivateRoute } from './auth/private-route'
import { MenuLabel } from './menu/components/menu-label'
import { MenuItem } from './menu/components/menu-item'
import { TerminalSetupPage } from './pages/admin/terminal-setup-page'
import { CheckInHome } from './pages/check-in/paired/admin/check-in-home'
import { LoyaltyDetailPage } from './pages/check-in/paired/admin/loyalty-detail-page'
import { PairedAddCustomerPage } from './pages/check-in/paired/paired-add-customer-page'
import { PairedEmailCheckInPage } from './pages/check-in/paired/paired-email-check-in-page'
import { PairedLoyaltyInfoPage } from './pages/check-in/paired/paired-loyalty-info-page'
import { PairedPhoneCheckInPage } from './pages/check-in/paired/paired-phone-check-in-page'
import { HeartlandCreateAlterationPage } from './pages/heartland/heartland-create-alteration-page'
import HeartlandOptInMoreInfoPage from './pages/heartland/heartland-opt-in-more-info-page'
import HeartlandOptInPage from './pages/heartland/heartland-opt-in-page'
import HeartlandOptInSuccessPage from './pages/heartland/heartland-opt-in-success-page'
import HeartlandRedemptionPage from './pages/heartland/heartland-redemption-page'
import LightspeedOptInMoreInfoPage from './pages/lightspeed/lightspeed-opt-in-more-info-page'
import LightspeedOptInPage from './pages/lightspeed/lightspeed-opt-in-page'
import LightspeedOptInSuccessPage from './pages/lightspeed/lightspeed-opt-in-success-page'
import LightspeedRedemptionPage from './pages/lightspeed/lightspeed-redemption-page'
import { useActiveStations } from './check-in/paired/queries'
import { PhoneCheckInPage } from './pages/check-in/paired/admin/phone-check-in-page'
import { EmailCheckInPage } from './pages/check-in/paired/admin/email-check-in-page'
import { PairedCheckInInfoPage } from './pages/check-in/paired/paired-check-in-info-page'

function CheckInApp() {
  const { handleLogOut } = React.useContext(AuthContext)

  const history = useHistory()
  const [presentAlert] = useIonAlert()

  const activeStations = useActiveStations()
  const isPos = activeStations.data?.some((s) => s.isPos)
  const isClient = activeStations.data?.some((s) => s.isClient)

  return (
    <>
      <IonMenu type="overlay" contentId="main" swipeGesture={isClient}>
        <IonContent
          forceOverscroll={false}
          style={{ '--padding-top': 0, '--padding-bottom': 0 }}
        >
          <IonList
            lines="none"
            className="flex h-full flex-col justify-between"
          >
            <div>
              {isPos ? (
                <MenuItem
                  route={{
                    title: 'Check-In Home',
                    path: '/check-in/paired/admin/home',
                    icon: home,
                    appSettings: ['clienteling', 'marketing'],
                    user: 'admin',
                  }}
                />
              ) : null}
              {isClient ? (
                <MenuItem
                  route={{
                    title: 'Paired Check-In',
                    path: '/check-in/paired/phone-check-in',
                    icon: person,
                    appSettings: ['clienteling', 'marketing'],
                    user: 'admin',
                  }}
                />
              ) : null}
              <MenuItem
                route={{
                  title: 'Station Info',
                  path: '/check-in/paired/check-in-info',
                  icon: desktop,
                  appSettings: ['clienteling', 'marketing'],
                }}
              />
            </div>

            <IonMenuToggle>
              <IonItem
                detail={false}
                button
                onClick={() => {
                  presentAlert({
                    header: 'Log Out?',
                    message:
                      'Are you sure you want to log out? This will end your check-in session.',
                    backdropDismiss: false,
                    buttons: [
                      'Cancel',
                      {
                        text: 'Log Out',
                        handler: () => {
                          handleLogOut(history)
                        },
                      },
                    ],
                  })
                }}
              >
                <IonIcon slot="start" icon={logOut} />
                <MenuLabel>Logout</MenuLabel>
              </IonItem>
            </IonMenuToggle>
          </IonList>
        </IonContent>
      </IonMenu>

      <IonRouterOutlet id="main">
        {/* Admin Routes */}
        <Route exact path="/check-in/paired/admin/home">
          <CheckInHome />
        </Route>
        <Route exact path="/check-in/paired/admin/phone-check-in">
          <PhoneCheckInPage />
        </Route>
        <Route exact path="/check-in/paired/admin/email-check-in">
          <EmailCheckInPage />
        </Route>
        <Route exact path="/check-in/paired/admin/add-customer">
          <PairedAddCustomerPage />
        </Route>

        {/* Paired Check-In */}
        <PrivateRoute
          exact
          path="/check-in/paired/phone-check-in"
          requiredSettings={['clienteling', 'marketing']}
        >
          <PairedPhoneCheckInPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/check-in/paired/email-check-in"
          requiredSettings={['clienteling', 'marketing']}
        >
          <PairedEmailCheckInPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/check-in/paired/add-customer"
          requiredSettings={['clienteling', 'marketing']}
        >
          <PairedAddCustomerPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/check-in/paired/loyalty-info"
          requiredSettings={['clienteling', 'marketing']}
        >
          <PairedLoyaltyInfoPage />
        </PrivateRoute>

        <PrivateRoute
          exact
          path="/check-in/paired/check-in-info"
          requiredSettings={['clienteling', 'marketing']}
        >
          <PairedCheckInInfoPage />
        </PrivateRoute>

        <Route exact path="/check-in/paired/admin/terminal-setup">
          <TerminalSetupPage />
        </Route>

        <Route exact path="/check-in/paired/admin/redeem-loyalty">
          <LoyaltyDetailPage />
        </Route>

        {/* Heartland Routes */}
        <Route exact path="/heartland/:groupId/redeem-loyalty">
          <HeartlandRedemptionPage />
        </Route>
        <Route exact path="/heartland/:groupId/opt-in">
          <HeartlandOptInPage />
        </Route>
        <Route exact path="/heartland/:groupId/opt-in/success">
          <HeartlandOptInSuccessPage />
        </Route>
        <Route exact path="/heartland/:groupId/opt-in/more-info">
          <HeartlandOptInMoreInfoPage />
        </Route>
        <Route exact path="/heartland/alteration/create/:clientId">
          <HeartlandCreateAlterationPage />
        </Route>
        {/* Lightspeed Routs */}
        <Route exact path="/lightspeed/redeem-loyalty">
          <LightspeedRedemptionPage />
        </Route>
        <Route exact path="/lightspeed/opt-in">
          <LightspeedOptInPage />
        </Route>
        <Route exact path="/lightspeed/opt-in/success">
          <LightspeedOptInSuccessPage />
        </Route>
        <Route exact path="/lightspeed/opt-in/more-info">
          <LightspeedOptInMoreInfoPage />
        </Route>

        {isPos ? (
          <Route>
            <Redirect to="/check-in/paired/admin/home" />
          </Route>
        ) : isClient ? (
          <Route>
            <Redirect to="/check-in/paired/phone-check-in" />
          </Route>
        ) : (
          <Route>
            <Redirect to="/check-in/paired/admin/terminal-setup" />
          </Route>
        )}
      </IonRouterOutlet>
    </>
  )
}

export { CheckInApp }
